import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useBrandOptional } from '@/lib/brand-provider'
import { useAuth } from '@/lib/config/auth-provider'
import { useLogoNeedsInversion } from '@/lib/hooks/use-logo-needs-inversion'
import { useTheme } from '@/lib/theme-provider'

import {
  resolveStudyDocPrintBrand,
  resolveStudyDocPrintLogoInvert,
  type StudyDocPrintBrandSource,
} from './resolveStudyDocPrintBrand'

export interface StudyDocPrintBrandOverride extends Partial<StudyDocPrintBrandSource> {
  brandLogoNeedsInversion?: boolean | null
  brandLogoOnDarkNeedsInversion?: boolean | null
}

export interface StudyDocPrintBrandView {
  logoSrc: string | null
  logoAlt: string
  logoNeedsInvert: boolean
  credit: string
  orgName: string
}

function useThemeOptional(): { resolvedTheme: string } | undefined {
  try {
    return useTheme()
  } catch {
    return undefined
  }
}

/**
 * Resolve the print letterhead from the live brand, or from a fixture override
 * used by the discardable preview bed and tests.
 *
 * Cache scope: one-shot, not cached.
 */
export function useStudyDocPrintBrand(
  override?: StudyDocPrintBrandOverride,
): StudyDocPrintBrandView {
  const brand = useBrandOptional()
  const { user } = useAuth()
  const { org } = useCurrentOrg()
  const theme = useThemeOptional()

  const whiteLabel =
    override?.whiteLabel ??
    (user?.featureFlags as { whiteLabel?: boolean } | undefined)?.whiteLabel ===
      true
  const isDark = override?.isDark ?? theme?.resolvedTheme === 'dark'
  const brandLogoUrl =
    override && Object.prototype.hasOwnProperty.call(override, 'brandLogoUrl')
      ? (override.brandLogoUrl ?? null)
      : (brand?.brandSettings.brandLogoUrl ?? brand?.brandLogoUrl ?? null)
  const brandLogoUrlOnDark =
    override && Object.prototype.hasOwnProperty.call(override, 'brandLogoUrlOnDark')
      ? (override.brandLogoUrlOnDark ?? null)
      : (brand?.brandLogoUrlOnDark ?? brand?.brandSettings.brandLogoUrlOnDark ?? null)

  const resolved = resolveStudyDocPrintBrand({
    whiteLabel,
    isDark,
    orgName: override?.orgName ?? org?.name,
    organizationName: override?.organizationName ?? user?.organizationName,
    companyName: override?.companyName ?? user?.companyName,
    brandLogoUrl,
    brandLogoUrlOnDark,
  })

  const invertServerFlag = resolved.hasDarkLogoOnly
    ? (override?.brandLogoOnDarkNeedsInversion ??
      brand?.brandSettings.brandLogoOnDarkNeedsInversion)
    : (override?.brandLogoNeedsInversion ??
      brand?.brandSettings.brandLogoNeedsInversion)
  const logoNeedsInversion = useLogoNeedsInversion(
    resolved.invertProbeSrc,
    invertServerFlag,
  )
  const logoNeedsInvert = resolveStudyDocPrintLogoInvert({
    isUsingOrgWordmark: resolved.isUsingOrgWordmark,
    isDark,
    hasDarkLogo: resolved.hasDarkLogo,
    hasDarkLogoOnly: resolved.hasDarkLogoOnly,
    logoNeedsInversion,
  })

  return {
    logoSrc: resolved.logoSrc,
    logoAlt: resolved.logoAlt,
    logoNeedsInvert,
    credit: resolved.credit,
    orgName: resolved.orgName,
  }
}
